import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  Dialog,
  Heading,
  Badge,
  Table,
  TextField,
} from '@radix-ui/themes';
import {
  Cross2Icon,
  UploadIcon,
  DownloadIcon,
  ReloadIcon,
} from '@radix-ui/react-icons';
import { toast } from 'react-hot-toast';
import { calendarApi } from '../services/api';

interface ExtractedName {
  first_name?: string;
  last_name?: string;
  name?: string;
}

interface MatchResult {
  name: ExtractedName;
  patient_ids: string[];
}

interface ImportResult {
  success: boolean;
  import_id: number;
  calendar_date: string;
  extracted_count: number;
  matched_count: number;
  matches: MatchResult[];
}

type DialogState = 'idle' | 'importing' | 'results' | 'error';

interface CalendarImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportComplete?: (importId: number, matchedPatientIds: string[]) => void;
}

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
const TEXT_TYPES = ['text/plain', 'text/csv'];

function getDisplayName(name: ExtractedName): string {
  if (name.name) return name.name;
  if (name.first_name && name.last_name) return `${name.first_name} ${name.last_name}`;
  if (name.first_name) return name.first_name;
  if (name.last_name) return name.last_name;
  return 'Unknown';
}

export default function CalendarImportDialog({
  open,
  onOpenChange,
  onImportComplete,
}: CalendarImportDialogProps) {
  const [state, setState] = useState<DialogState>('idle');
  const [result, setResult] = useState<ImportResult | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [calendarDate, setCalendarDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [pastedText, setPastedText] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = useCallback(() => {
    setState('idle');
    setResult(null);
    setErrorMsg('');
    setIsDragOver(false);
    setPastedText('');
    setCalendarDate(new Date().toISOString().slice(0, 10));
  }, []);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) reset();
      onOpenChange(isOpen);
    },
    [onOpenChange, reset]
  );

  const processText = useCallback(async () => {
    if (!pastedText.trim()) {
      setErrorMsg('Please paste some calendar text or upload a file.');
      setState('error');
      return;
    }

    if (!calendarDate) {
      setErrorMsg('Please select a calendar date.');
      setState('error');
      return;
    }

    setState('importing');

    try {
      const data = await calendarApi.importCalendar({
        content: pastedText,
        mime_type: 'text/plain',
        calendar_date: calendarDate,
        is_base64: false,
      });

      if (data.success) {
        setResult(data);
        setState('results');
        if (onImportComplete && data.matched_count > 0) {
          const allIds: string[] = data.matches.flatMap((m: MatchResult) => m.patient_ids);
          onImportComplete(data.import_id, [...new Set(allIds)]);
        }
      } else {
        setErrorMsg(data.error || 'Failed to import calendar data.');
        setState('error');
      }
    } catch (err: any) {
      const msg =
        err?.errorData?.error ||
        err?.errorData?.details ||
        err?.message ||
        'Failed to import calendar. Please try again.';
      setErrorMsg(msg);
      setState('error');
    }
  }, [pastedText, calendarDate, onImportComplete]);

  const processFile = useCallback(async (file: File) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setErrorMsg('Unsupported file type. Please upload a PDF, JPG, or PNG.');
      setState('error');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setErrorMsg('File is too large. Maximum size is 20 MB.');
      setState('error');
      return;
    }

    if (!calendarDate) {
      setErrorMsg('Please select a calendar date.');
      setState('error');
      return;
    }

    setState('importing');

    try {
      const base64 = await fileToBase64(file);
      const data = await calendarApi.importCalendar({
        content: base64,
        mime_type: file.type,
        calendar_date: calendarDate,
        is_base64: true,
      });

      if (data.success) {
        setResult(data);
        setState('results');
        if (onImportComplete && data.matched_count > 0) {
          const allIds: string[] = data.matches.flatMap((m: MatchResult) => m.patient_ids);
          onImportComplete(data.import_id, [...new Set(allIds)]);
        }
      } else {
        setErrorMsg(data.error || 'Failed to import calendar data.');
        setState('error');
      }
    } catch (err: any) {
      const msg =
        err?.errorData?.error ||
        err?.errorData?.details ||
        err?.message ||
        'Failed to import calendar. Please try again.';
      setErrorMsg(msg);
      setState('error');
    }
  }, [calendarDate, onImportComplete]);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) processFile(file);
    },
    [processFile]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) processFile(file);
      // Reset so re-selecting the same file fires onChange again
      e.target.value = '';
    },
    [processFile]
  );

  const handlePasteSubmit = useCallback(() => {
    processText();
  }, [processText]);

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content style={{ maxWidth: 800, maxHeight: '85vh', overflow: 'auto' }}>
        <Flex justify="between" align="center" mb="4">
          <Dialog.Title asChild>
            <Heading size="5">Import Calendar</Heading>
          </Dialog.Title>
          <Dialog.Close>
            <Button variant="ghost" color="gray" size="1">
              <Cross2Icon />
            </Button>
          </Dialog.Close>
        </Flex>

        {/* Idle — input options */}
        {state === 'idle' && (
          <Box>
            <Text size="2" style={{ display: 'block', marginBottom: 16, color: '#64748b' }}>
              Import your EHR calendar or schedule to filter patients by their appointments.
              You can paste text or upload a file.
            </Text>

            {/* Date picker */}
            <Box mb="4">
              <Text size="2" weight="medium" style={{ display: 'block', marginBottom: 8 }}>
                Calendar Date
              </Text>
              <input
                type="date"
                value={calendarDate}
                onChange={(e) => setCalendarDate(e.target.value)}
                style={{
                  padding: 8,
                  borderRadius: 6,
                  border: '1px solid #cbd5e1',
                  fontSize: 14,
                  fontFamily: 'inherit',
                }}
              />
            </Box>

            {/* Paste text area */}
            <Box mb="4">
              <Text size="2" weight="medium" style={{ display: 'block', marginBottom: 8 }}>
                Or paste calendar text
              </Text>
              <textarea
                value={pastedText}
                onChange={(e) => setPastedText(e.target.value)}
                placeholder="Paste calendar/schedule text here...&#10;&#10;Example:&#10;04/07/2026 09:00 AM - John Doe - Wellness Visit&#10;04/07/2026 10:30 AM - Jane Smith - Follow-up"
                style={{
                  width: '100%',
                  minHeight: 120,
                  padding: 12,
                  borderRadius: 6,
                  border: '1px solid #cbd5e1',
                  fontSize: 14,
                  fontFamily: 'inherit',
                  resize: 'vertical',
                }}
              />
              <Button
                variant="solid"
                mt="2"
                onClick={handlePasteSubmit}
                disabled={!pastedText.trim()}
              >
                Import from Text
              </Button>
            </Box>

            {/* Divider */}
            <Flex align="center" gap="2" mb="4">
              <Box style={{ flex: 1, height: 1, backgroundColor: '#e2e8f0' }} />
              <Text size="2" color="gray">
                or
              </Text>
              <Box style={{ flex: 1, height: 1, backgroundColor: '#e2e8f0' }} />
            </Flex>

            {/* File upload */}
            <Box
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: `2px dashed ${isDragOver ? '#3b82f6' : '#cbd5e1'}`,
                borderRadius: 12,
                padding: '32px 24px',
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: isDragOver ? '#eff6ff' : '#f8fafc',
                transition: 'all 0.15s',
              }}
            >
              <UploadIcon width="28" height="28" style={{ margin: '0 auto 12px', color: '#64748b' }} />
              <Text size="3" weight="medium" style={{ display: 'block', color: '#334155' }}>
                Drag & drop or click to browse
              </Text>
              <Text size="2" style={{ display: 'block', marginTop: 4, color: '#94a3b8' }}>
                Accepts PDF, JPG, or PNG (max 20 MB)
              </Text>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
            </Box>
          </Box>
        )}

        {/* Importing */}
        {state === 'importing' && (
          <Box style={{ textAlign: 'center', padding: '48px 24px' }}>
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mx-auto mb-4" />
            <Text size="3" style={{ color: '#475569' }}>
              Extracting patient names...
            </Text>
          </Box>
        )}

        {/* Error */}
        {state === 'error' && (
          <Box style={{ textAlign: 'center', padding: '36px 24px' }}>
            <Text size="3" color="red" style={{ display: 'block', marginBottom: 16 }}>
              {errorMsg}
            </Text>
            <Button variant="outline" onClick={reset}>
              <ReloadIcon width="16" height="16" /> Try Again
            </Button>
          </Box>
        )}

        {/* Results */}
        {state === 'results' && result && (
          <Box>
            {/* Summary */}
            <Flex gap="3" mb="4" wrap="wrap">
              <SummaryCard
                label="Names Extracted"
                value={result.extracted_count}
                color="#3b82f6"
              />
              <SummaryCard
                label="Patients Matched"
                value={result.matched_count}
                color="#22c55e"
              />
            </Flex>

            {/* Results table */}
            <Box style={{ overflowX: 'auto', maxHeight: '40vh', overflowY: 'auto' }}>
              <Table.Root size="1" variant="surface">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeaderCell>Extracted Name</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Matched Patients</Table.ColumnHeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {result.matches.map((match, i) => (
                    <Table.Row key={i}>
                      <Table.Cell style={{ fontWeight: 500 }}>
                        {getDisplayName(match.name)}
                      </Table.Cell>
                      <Table.Cell>
                        {match.patient_ids.length > 0 ? (
                          <Flex gap="1" wrap="wrap">
                            {match.patient_ids.map((id) => (
                              <Badge key={id} color="green">
                                {id}
                              </Badge>
                            ))}
                          </Flex>
                        ) : (
                          <Badge color="gray">No match</Badge>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>

            {/* Action buttons */}
            <Flex gap="2" mt="4" justify="end">
              <Button variant="outline" onClick={reset}>
                <UploadIcon width="16" height="16" /> Import Another
              </Button>
              <Button variant="solid" onClick={() => handleOpenChange(false)}>
                Done
              </Button>
            </Flex>
          </Box>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}

// --- Helpers ---

function SummaryCard({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <Box
      style={{
        flex: '1 1 140px',
        minWidth: 140,
        padding: 16,
        borderRadius: 8,
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
      }}
    >
      <Flex direction="column" align="center" gap="1">
        <Text size="5" weight="bold" style={{ color }}>
          {value}
        </Text>
        <Text size="1" style={{ color: '#64748b' }}>
          {label}
        </Text>
      </Flex>
    </Box>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      // Strip the data:...;base64, prefix
      const base64 = dataUrl.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
